import type {
  SupportingItemViewModel,
  TaxInfoGatheringViewModel,
} from '@/features/jobs/details/tax-type/info-gathering/TaxInfoGatheringViewModel'
import { ButtonGroup } from '@/components/ButtonGroup'
import { Button, type ButtonProps } from '@/components/Button'
import { observer } from 'mobx-react-lite'
import { useProvideWizardBottomBarPrimaryButton } from '@/components/Wizard'
import { useMemo } from 'react'
import { links } from '@/utils/link'
import { useIsSpacious } from '@/hooks/viewport-hooks'

export interface TaxInfoGatheringWizardContentButtonGroupContainerProps {
  taxInfoGatheringVm: TaxInfoGatheringViewModel
  itemVm: SupportingItemViewModel
  isSkeleton?: boolean
  /**
   * When using a form, provide the form ID.
   */
  formId?: string
  /**
   * Will be called only if no {@link formId} is specified and
   * the {@link variant} is `save`. When using a form, the form's
   * submit-handler should be used.
   */
  onSavePress?: () => unknown
  /**
   * Whether the save action is pending.
   */
  isSavePending?: boolean
  /**
   * Whether the group should show saving as an option,
   * or if it's only for navigation. For example, some items like documents
   * and dependents are lists, so there's no natural "save" option that pertains
   * to the form as a whole.
   */
  variant: 'save' | 'nav-only'
}

/**
 * Button group container for the info gathering wizard content that shows the
 * proper buttons based on the state of the current supporting item
 * being viewed/edited.
 */
export const TaxInfoGatheringWizardContentButtonGroupContainer = observer(
  function TaxInfoGatheringWizardContentButtonGroupContainer({
    formId,
    isSkeleton,
    onSavePress,
    isSavePending,
    taxInfoGatheringVm,
    itemVm,
    variant,
  }: TaxInfoGatheringWizardContentButtonGroupContainerProps) {
    const isCompleteInfoGatheringPending =
      taxInfoGatheringVm.confirmCompleteInfoGathering.pending

    const saveLabel = itemVm.isCompleted
      ? 'Save changes & continue'
      : 'Save & continue'

    const continueVariant =
      variant === 'save' ? 'text' : !itemVm.isCompleted ? 'outline' : 'default'

    const isLastItem = taxInfoGatheringVm.pageVm.isLastItem(itemVm)
    const isEnabledAndTxfJob = taxInfoGatheringVm.txfJobVm.isEnabledAndTxfJob
    const showContinueToTaxPrepOrganizer =
      taxInfoGatheringVm.showContinueToTaxPrepOrganizer

    const primarySaveButtonProps: ButtonProps = useMemo(
      () => ({
        label: saveLabel,
        form: formId,
        type: formId ? 'submit' : 'button',
        onPress: formId ? onSavePress : undefined,
        isPending: isSavePending,
        isDisabled: isCompleteInfoGatheringPending,
        testId: 'info-gathering-save-button',
      }),
      [
        saveLabel,
        formId,
        onSavePress,
        isSavePending,
        isCompleteInfoGatheringPending,
      ],
    )

    const continueButtonProps: ButtonProps = useMemo(
      () => ({
        variant: continueVariant,
        label: itemVm.isCompleted ? 'Continue' : 'Skip for now',
        onPress: taxInfoGatheringVm.pageVm.selectNextItem,
        isDisabled: isSavePending || isCompleteInfoGatheringPending,
        testId: 'info-gathering-continue-button',
      }),
      [
        continueVariant,
        isCompleteInfoGatheringPending,
        isSavePending,
        itemVm.isCompleted,
        taxInfoGatheringVm.pageVm.selectNextItem,
      ],
    )

    const connectWithProButtonProps: ButtonProps = useMemo(
      () => ({
        variant: continueVariant,
        label: isEnabledAndTxfJob ? 'Continue' : 'Connect with a Pro',
        onPress: taxInfoGatheringVm.tryCompleteInfoGathering,
        isDisabled: isSavePending,
        isPending: isCompleteInfoGatheringPending,
        testId: 'info-gathering-connect-with-pro-button',
      }),
      [
        continueVariant,
        isCompleteInfoGatheringPending,
        isEnabledAndTxfJob,
        isSavePending,
        taxInfoGatheringVm.tryCompleteInfoGathering,
      ],
    )

    const continueToTaxPrepOrganizerButtonProps: ButtonProps = useMemo(
      () => ({
        variant: continueVariant,
        label: 'Continue',
        to: links.taxPrepOrganizer(taxInfoGatheringVm.job.id),
        testId: 'info-gathering-continue-to-tax-prep-organizer-button',
      }),
      [continueVariant, taxInfoGatheringVm.job.id],
    )

    const isSpacious = useIsSpacious()

    const continueOrConnectWithProButtonProps = showContinueToTaxPrepOrganizer
      ? continueToTaxPrepOrganizerButtonProps
      : connectWithProButtonProps

    useProvideWizardBottomBarPrimaryButton(
      variant === 'save'
        ? primarySaveButtonProps
        : isLastItem
          ? continueOrConnectWithProButtonProps
          : continueButtonProps,
    )

    return (
      <ButtonGroup stretch stacked>
        {variant === 'save' && (
          <Button
            {...primarySaveButtonProps}
            label={saveLabel}
            isSkeleton={isSkeleton}
          />
        )}

        {!isSpacious && taxInfoGatheringVm.isMeasureOneEnabled && (
          <Button
            iconLeft="upload"
            variant="outline"
            label="Auto-import documents"
            to={taxInfoGatheringVm.measureOneVm.autoImportDocumentsLink}
          />
        )}

        {isLastItem ? (
          <Button
            {...continueOrConnectWithProButtonProps}
            isSkeleton={isSkeleton}
          />
        ) : (
          <Button {...continueButtonProps} isSkeleton={isSkeleton} />
        )}
      </ButtonGroup>
    )
  },
)
